/**
 * Screen 8 — in-service modification (BOE Fifth).
 *
 * **One field at a time**, as the `Portal · Modificación (modal)` frame draws
 * it: pick the field, see the current value beside the new one, state a reason,
 * and read the history row that will be written before committing it.
 *
 * The Resolution recognises exactly two ways to change a DeCA in service, and
 * both are buttons here rather than tabs, so neither is hidden behind the other:
 *
 *  - **Guardar · mismo QR** → `POST /decas/{id}/modifications`. Same document
 *    number, same CSV, **same URL and therefore the same QR** (Fifth.1). A new
 *    immutable version is appended, one history entry per changed field, and the
 *    previous value is retained and printed marked as no longer valid.
 *  - **Regenerar como documento nuevo** → `POST /decas/{id}/supersede`. A new
 *    document with a new number, CSV, URL and QR; the original is retained and
 *    becomes `ANULADO / SUPERSEDED` (Fifth.2).
 *
 * The parties (fields 1–6) are **not** amendable in place: a change of shipper
 * or carrier is a different transport contract, and it would undermine a grouped
 * document's validity. Choosing one disables the in-place button and says why —
 * the design spec's own "mandatory, not optional" delta.
 *
 * A consignment field is sent as the **whole replacement set** with that one
 * value swapped: the contract takes `consignments` entire and diffs it
 * server-side to produce the history entries, so a partial array would read as a
 * deletion of the rest.
 *
 * The service date is the document's, and the server writes it to every envío. On a DeCA whose envíos
 * print different dates (a grouping keeps each one's own) that date says it moves every envío, and
 * each envío's own date is a target too, sent in that whole set with only that envío's date stated:
 * the others, sent without one, keep theirs (#135). The preview then shows what the server records,
 * one row per envío whose printed date changes.
 *
 * A finished document (#180) is amended in place only: the dialog offers no party field and no
 * «Emitir documento nuevo», and says how the date returns it to service.
 *
 * Both paths close on the same reminder: BOE Fifth requires the amended or new
 * PDF to **reach the driver**. In-place keeps the QR, so the driver's scan
 * resolves to the new content; supersede does not, so the wording is emphatic.
 *
 * ⚠️ **Goods only, and the type system says so here only because `Deca` does.** The
 * route sheet's amendment is `PassengerModifyDialog`, a sibling rather than a
 * configuration of this file. The plan for Task 10 proposed passing the dictionary and
 * the modifiable set in as props; the dictionary turned out to be one of eight goods
 * couplings in this component. It also walks `deca.consignments` (a sheet has
 * `services`), reads `FieldDef.modifiableInPlace` and `FieldDef.control` — neither
 * exists on `PassengerFieldDef`, deliberately — calls two `/decas/{decaId}/…` path
 * literals that `openapi-fetch` types *by literal*, invalidates the goods cache family,
 * reads the 422 through `validationProblemOf` and renders `ValidationSummary` (a route
 * sheet's 422 is flat and answers `null` there), and navigates to `/decas/$decaId`.
 * Parameterising all of that leaves a component whose every behaviour is a prop. The two
 * create forms are split the same way and for the same reason.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  errorsByField,
  ifMatch,
  unwrap,
  UNIDADES_MEDIDA,
  type Deca,
  type FieldError as ApiFieldError,
  type PartyErrors,
  type UnidadMedida,
} from '@deca/api'

import { api } from '../app/api.ts'
import { apiErrorMessage, isConflict, validationProblemOf } from '../app/errors.ts'
import { useAuth } from '../app/auth.tsx'
import { useInvalidateDeca } from '../app/queries.ts'
import { canModify } from '../deca/estado.ts'
import { consignmentDatesDiffer, consignmentDateValue } from '../deca/consignmentDates.ts'
import { FIELDS, fieldsForSection, qualifiedFieldLabel, type FieldDef } from '../deca/fields.ts'
import {
  formatDateTime,
  joinServiceDate,
  localInputToRfc3339,
  SERVICE_DATE_BOUNDS,
  serviceDateValue,
} from '../deca/format.ts'
import { Button, Notice } from '../ui/primitives.tsx'
import { Dialog } from '../ui/Dialog.tsx'
import { SelectInput, TextAreaInput, TextInput } from '../ui/form.tsx'
import { ValidationSummary } from '../ui/ValidationSummary.tsx'
import { SettingBox } from '../ui/SettingBox.tsx'
import { useToast } from '../ui/Toast.tsx'

const PARTY_COLUMNS = new Set(
  [...fieldsForSection('cargador'), ...fieldsForSection('transportista')].map((f) => f.column),
)

/** One amendable thing: a document field, or a field of one consignment. */
interface Target {
  key: string
  def: FieldDef
  /** 1-based, for a consignment field; absent for a document field. */
  seq?: number
  /**
   * The value the controls start from. Empty for the document's date on a DeCA whose envíos print
   * different dates: it has no one value, and envío 1's, pre-filled, would move the others on save.
   */
  current: string
  /** The document's date on a DeCA whose envíos print different dates: it moves every envío (#135). */
  everyConsignment?: boolean
}

export function ModifyDialog({
  deca,
  etag,
  open,
  onClose,
}: {
  deca: Deca
  etag: string | null
  open: boolean
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateDeca()
  const { principal } = useAuth()

  // Not in service. The dialog opens only when `canAmend`, so that is FINALIZADO; and «Emitir
  // documento nuevo» is offered exactly when `canModify` holds, as `canAmend`'s docblock says (#180).
  const finished = !canModify(deca)
  const hintId = useId()
  // A finished document is amended in place only (#180), so it offers only `modifiableInPlace` fields,
  // as the route sheet's dialog does. The others are the six party fields `deca.test.ts` locks (the
  // set `PARTY_COLUMNS` holds), and a party needs a new document, which «Duplicar» makes.
  const targets = useMemo(
    () => targetsOf(deca).filter((candidate) => !finished || candidate.def.modifiableInPlace),
    [deca, finished],
  )
  // Open on something amendable in place. Starting on a party field would put
  // the dialog into its one blocked state before the user has done anything.
  const initial = targets.find((candidate) => candidate.def.modifiableInPlace) ?? targets[0]
  const [targetKey, setTargetKey] = useState(() => initial?.key ?? '')
  /**
   * Pre-filled with the current value, not blank. Blank would read as a change
   * the moment the dialog opened, and it would leave no way to say "clear this
   * optional field" as distinct from "I have not typed yet".
   */
  const [newValue, setNewValue] = useState(() => initial?.current ?? '')
  /** «Rígido» as the amendment would leave it — only offered, and only sent, with the trailer (spec D11). */
  const [newRigido, setNewRigido] = useState(() => deca.rigido ?? false)
  const [reason, setReason] = useState('')
  /**
   * A missing reason is said on the reason field, not in the notice at the dialog's head (#111's
   * review). On a phone the dialog is one tall column, and that notice is out of view once the user
   * has scrolled to the reason, so the save seemed to do nothing.
   */
  const [reasonError, setReasonError] = useState<string | null>(null)
  const reasonField = useRef<HTMLTextAreaElement>(null)
  const [serverErrors, setServerErrors] = useState<Record<string, ApiFieldError>>({})
  const [partyErrors, setPartyErrors] = useState<PartyErrors[] | undefined>()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  /**
   * A refusal is drawn at the dialog's head, and on a phone the user has scrolled that out of view
   * by the time they tap a button: the save seemed to do nothing (#134). So it is brought into view,
   * as *Empresa* does. `nearest` leaves one already in view where it is; `?.` because jsdom has no
   * `scrollIntoView`. The missing reason is not among them: that one is said on its field.
   */
  const refusalNotice = useRef<HTMLDivElement>(null)
  const refusalSummary = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (error) refusalNotice.current?.scrollIntoView?.({ block: 'nearest' })
  }, [error])
  useEffect(() => {
    if (partyErrors) refusalSummary.current?.scrollIntoView?.({ block: 'nearest' })
  }, [partyErrors])

  // Stamped when the dialog opens rather than read on every render: the preview
  // is of one row that will be written, not a running clock.
  const [openedAt] = useState(() => new Date().toISOString())

  const target = targets.find((candidate) => candidate.key === targetKey) ?? targets[0]
  const isParty = target ? PARTY_COLUMNS.has(target.def.column) : false
  const isTrailer = target?.def.column === 'matricula_remolque'
  const flagChanged = isTrailer && newRigido !== (deca.rigido ?? false)
  const changed = Boolean(target) && (newValue !== (target?.current ?? '') || flagChanged)
  const fieldError = target ? serverErrors[target.def.column]?.message : undefined

  function choose(key: string) {
    setTargetKey(key)
    // The new value belongs to the field it was typed for.
    setNewValue(targets.find((candidate) => candidate.key === key)?.current ?? '')
    setNewRigido(deca.rigido ?? false)
    setServerErrors({})
    setPartyErrors(undefined)
    setError(null)
  }

  async function submit(mode: 'inPlace' | 'supersede') {
    if (!target) return
    if (reason.trim().length < 3) {
      setReasonError(t('modify.reasonRequired'))
      // Focus brings the field into view, and on a phone opens the keyboard on it.
      reasonField.current?.focus()
      return
    }
    setBusy(true)
    setError(null)
    setServerErrors({})
    setPartyErrors(undefined)

    try {
      const body = changeBody(deca, target, newValue, newRigido)
      if (mode === 'inPlace') {
        const result = await unwrap(
          api.POST('/api/v1/decas/{decaId}/modifications', {
            params: { path: { decaId: deca.id }, header: ifMatch(etag) },
            body: { reason: reason.trim(), changes: body },
          }),
        )
        invalidate(result.id)
        toast.success(t(finished ? (result.estado === 'MODIFICADO' ? 'modify.savedReturned' : 'modify.savedStillFinished') : 'modify.savedInPlace'))
        onClose()
      } else {
        const result = await unwrap(
          api.POST('/api/v1/decas/{decaId}/supersede', {
            params: { path: { decaId: deca.id }, header: ifMatch(etag) },
            body: { reason: reason.trim(), changes: body, generate: true },
          }),
        )
        invalidate(deca.id)
        invalidate(result.id)
        toast.success(t('modify.savedSupersede', { documentNumber: result.documentNumber ?? '' }))
        onClose()
        void navigate({ to: '/decas/$decaId', params: { decaId: result.id } })
      }
    } catch (caught) {
      const problem = validationProblemOf(caught)
      if (problem) {
        setServerErrors(errorsByField(problem))
        setPartyErrors(problem.partyErrors)
      } else if (isConflict(caught)) {
        setError(t('modify.versionConflict'))
      } else {
        setError(apiErrorMessage(caught, t))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      busy={busy}
      title={t('modify.title', { documentNumber: deca.documentNumber ?? '' })}
      subtitle={t(finished ? 'modify.subtitleFinished' : 'modify.subtitle')}
      onClose={onClose}
      footer={
        <>
          {/* The alternative, stated beside the buttons rather than hidden
              behind the tab the user did not pick. */}
          {finished ? null : <p className="small muted dialog__footnote">{t('modify.alternative')}</p>}
          {/* In place first, and not by the desktop convention that puts the
              primary action last: the two are not peers. One appends a version
              behind the same QR, the other retires this document and issues a
              different one. Their testers asked for this order on 2026-09-18
              because the amendment is their ordinary case; it is also the order
              in which the heavier act is not what the eye lands on first. */}
          <Button
            variant="primary"
            icon="qr"
            loading={busy}
            disabled={busy || !changed || isParty}
            title={isParty ? t('modify.partiesLocked') : undefined}
            onClick={() => void submit('inPlace')}
          >
            {t('modify.submitInPlace')}
          </Button>
          {finished ? null : (
            <Button
              icon="refresh"
              loading={busy}
              disabled={busy || !changed}
              onClick={() => void submit('supersede')}
            >
              {t('modify.submitSupersede')}
            </Button>
          )}
        </>
      }
    >
      <Notice tone="info">{t('modify.methodInPlaceHelp')}</Notice>
      {finished ? (
        // Read with the picker, which takes the focus when the dialog opens (#180).
        <div id={hintId}>
          <Notice tone="info">{t('modify.finishedHint')}</Notice>
        </div>
      ) : null}

      {isParty ? <Notice tone="warning">{t('modify.partiesLocked')}</Notice> : null}
      {error ? (
        <div ref={refusalNotice}>
          <Notice tone="danger">{error}</Notice>
        </div>
      ) : null}
      {partyErrors ? (
        <div ref={refusalSummary}>
          <ValidationSummary partyErrors={partyErrors} />
        </div>
      ) : null}

      <SelectInput
        label={t('modify.fieldToModify')}
        full
        value={targetKey}
        {...(finished ? { 'aria-describedby': hintId } : {})}
        onChange={(event) => choose(event.currentTarget.value)}
      >
        {targets.map((candidate) => (
          <option key={candidate.key} value={candidate.key}>
            {candidate.everyConsignment
              ? `${qualifiedFieldLabel(candidate.def, t)} · ${t('modify.everyConsignment')}`
              : qualifiedFieldLabel(candidate.def, t, candidate.seq)}
          </option>
        ))}
      </SelectInput>

      {target?.everyConsignment ? (
        <Notice tone="info">{t('modify.dateMovesEveryConsignment', { picker: t('modify.fieldToModify') })}</Notice>
      ) : null}

      {target ? (
        <div className="form-grid form-grid--pair">
          <TextInput
            label={t('modify.currentValue')}
            value={
              target.everyConsignment
                ? t('modify.datesDiffer')
                : isTrailer && deca.rigido
                  ? t('fields.matricula_remolque.rigidoPrinted')
                  : target.current || t('common.none')
            }
            readOnly
            disabled
          />
          <NewValueControl
            def={target.def}
            value={newValue}
            error={fieldError}
            disabled={isTrailer && newRigido}
            onChange={setNewValue}
          />
        </div>
      ) : null}

      {isTrailer ? (
        <SettingBox
          label={t('fields.matricula_remolque.rigido')}
          help={t('fields.matricula_remolque.rigidoHelp')}
          checked={newRigido}
          onChange={(checked) => {
            setNewRigido(checked)
            if (checked) setNewValue('')
          }}
        />
      ) : null}

      <TextAreaInput
        label={t('modify.reason')}
        help={t('modify.reasonHelp')}
        placeholder={t('modify.reasonPlaceholder')}
        required
        rows={2}
        ref={reasonField}
        error={reasonError ?? undefined}
        value={reason}
        onChange={(event) => {
          setReason(event.currentTarget.value)
          setReasonError(null)
        }}
      />

      {/* What Fifth.1 will record, before it is recorded. */}
      <div className="history-preview">
        <span className="history-preview__label">{t('modify.willRecord')}</span>
        {changed && target ? (
          recordedRows(deca, target, newValue).map((row) => (
            <span key={row.seq ?? 'document'} className="history-preview__row">
              <span className="tabular">{formatDateTime(openedAt, i18n.language)}</span>
              <span>·</span>
              <span>{qualifiedFieldLabel(target.def, t, row.seq)}</span>
              <span>·</span>
              <span>
                <s>{isTrailer && deca.rigido ? t('fields.matricula_remolque.rigidoPrinted') : row.previous || '—'}</s>{' → '}
                {isTrailer && newRigido ? t('fields.matricula_remolque.rigidoPrinted') : newValue || '—'}
              </span>
              <span>·</span>
              <span>{principal?.fullName ?? principal?.email}</span>
            </span>
          ))
        ) : (
          <span className="small muted">{t('modify.previewEmpty')}</span>
        )}
      </div>

      <Notice tone="warning" title={t('create.deliverReminder')}>
        <span className="small">{t('modify.deliverInPlace')}</span>
      </Notice>
    </Dialog>
  )
}

function NewValueControl({
  def,
  value,
  error,
  disabled,
  onChange,
}: {
  def: FieldDef
  value: string
  error?: string
  /** The trailer while «Rígido» is ticked: no plate to type (spec D11). */
  disabled?: boolean
  onChange: (next: string) => void
}) {
  const { t } = useTranslation()
  const label = t('modify.newValue')

  if (def.control === 'select') {
    return (
      <SelectInput label={label} error={error} value={value} onChange={(e) => onChange(e.currentTarget.value)}>
        <option value="">{t('common.none')}</option>
        {UNIDADES_MEDIDA.map((unit) => (
          <option key={unit} value={unit}>
            {unit}
          </option>
        ))}
      </SelectInput>
    )
  }

  if (def.control === 'textarea') {
    return (
      <TextAreaInput
        label={label}
        error={error}
        rows={2}
        maxLength={def.maxLength}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    )
  }

  // The service date, and the hour that is optional beside it since 2026-09-18. Two controls
  // because `datetime-local` cannot be left half-empty, and empty is what "no time of day" is:
  // Art. 6(f) requires the *fecha* and names no hour. The value carried between them is the same
  // `YYYY-MM-DD[THH:MM]` the create form uses, so `changeBody` reads one shape, not two.
  if (def.control === 'date') {
    const date = value.slice(0, 10)
    const time = value.length > 10 ? value.slice(11, 16) : ''
    return (
      <div className="form-grid">
        <TextInput
          label={label}
          error={error}
          type="date"
          {...SERVICE_DATE_BOUNDS}
          value={date}
          onChange={(e) => onChange(joinServiceDate(e.currentTarget.value, time))}
        />
        <TextInput
          label={t('fields.fecha_realizacion.hora')}
          help={t('fields.fecha_realizacion.horaHelp')}
          type="time"
          value={time}
          onChange={(e) => onChange(joinServiceDate(date, e.currentTarget.value))}
        />
      </div>
    )
  }

  return (
    <TextInput
      label={label}
      error={error}
      type={def.control === 'number' ? 'number' : 'text'}
      maxLength={def.maxLength}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  )
}

/**
 * Every amendable thing on this document, in the dictionary's order.
 *
 * A service date is carried as the two controls carry it: `YYYY-MM-DD`, plus `THH:MM` only when a
 * time was given. A date-only document opens with an empty time box rather than `00:00`, so nobody
 * has to notice and clear a midnight they never typed. On a DeCA whose envíos print different dates,
 * each envío's date is offered among its fields, in field 9's place (#135).
 */
function targetsOf(deca: Deca): Target[] {
  const out: Target[] = []
  const datesDiffer = consignmentDatesDiffer(deca)

  for (const def of FIELDS) {
    if (def.scope !== 'document') continue
    if (def.control === 'date') {
      out.push(
        datesDiffer
          ? { key: def.column, def, current: '', everyConsignment: true }
          : { key: def.column, def, current: serviceDateValue(deca.fecha_realizacion, deca.fechaRealizacionHasTime) },
      )
      continue
    }
    const raw = (deca as unknown as Record<string, unknown>)[def.column]
    out.push({ key: def.column, def, current: String(raw ?? '') })
  }

  deca.consignments.forEach((row, index) => {
    for (const def of FIELDS) {
      if (datesDiffer && def.control === 'date') {
        out.push({ key: `${index}.${def.column}`, def, seq: index + 1, current: consignmentDateValue(row, deca) })
        continue
      }
      if (def.scope !== 'consignment') continue
      const raw = (row as unknown as Record<string, unknown>)[def.column]
      out.push({ key: `${index}.${def.column}`, def, seq: index + 1, current: String(raw ?? '') })
    }
  })

  return out
}

/**
 * The Historial rows the amendment will write, as the server writes them: one for the field, with the
 * value it replaces. The document's date on a DeCA whose envíos print different dates is written to
 * every envío, and recorded once for each envío whose printed date it changes, under its number
 * (BOE Fifth.1, #135); an envío that already prints the date typed records nothing.
 */
function recordedRows(deca: Deca, target: Target, newValue: string): Array<{ seq?: number; previous: string }> {
  if (!target.everyConsignment) return [{ seq: target.seq, previous: target.current }]
  return deca.consignments.flatMap((row, index) => {
    const previous = consignmentDateValue(row, deca)
    return previous === newValue ? [] : [{ seq: index + 1, previous }]
  })
}

/**
 * The request body for changing exactly this one field.
 *
 * A consignment field goes back as the **whole** `consignments` array with the
 * one value replaced — the contract diffs the set server-side, so sending only
 * the edited row would read as deleting the others. An envío's date is stated on
 * that envío alone, with its flag; the others go back without a date, and an
 * envío sent without one keeps its own.
 */
function changeBody(deca: Deca, target: Target, newValue: string, newRigido: boolean): Record<string, unknown> {
  if (target.seq == null) {
    if (target.def.column === 'matricula_remolque') {
      // The plate and «Rígido» are one change to field 14 (spec D5): the flag rides with the plate,
      // and only when it moves, so a plate-for-plate amendment sends what it always sent.
      return {
        matricula_remolque: newRigido ? null : newValue || '',
        ...(newRigido !== (deca.rigido ?? false) ? { rigido: newRigido } : {}),
      }
    }
    if (target.def.control === 'date') {
      // The flag travels with the date: amending one without the other would leave a timed
      // document carrying a calendar day, or print a midnight the user never typed.
      return {
        [target.def.column]: localInputToRfc3339(newValue),
        fechaRealizacionHasTime: newValue.length > 10,
      }
    }
    const value = newValue || (target.def.mandatory ? '' : null)
    return { [target.def.column]: value }
  }

  const index = target.seq - 1
  return {
    consignments: deca.consignments.map((row, position) => {
      const base = {
        origen_domicilio: row.origen_domicilio,
        destino_domicilio: row.destino_domicilio,
        naturaleza_mercancia: row.naturaleza_mercancia,
        peso_magnitud: Number(row.peso_magnitud),
        unidad_medida: row.unidad_medida as UnidadMedida,
      }
      if (position !== index) return base
      if (target.def.control === 'date') {
        return { ...base, fecha_realizacion: localInputToRfc3339(newValue), fechaRealizacionHasTime: newValue.length > 10 }
      }
      return {
        ...base,
        [target.def.column]:
          target.def.control === 'number' ? Number(newValue) : (newValue as unknown),
      }
    }),
  }
}
